import chevironRight from "$/cheviron-right.svg";
import a1 from "$/a1.svg";
import { SwiperXBox } from "@/SwiperX";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import useMyPageMain from "^/customer/myPage/useMyPageMain";
import { Dialog } from "@/Dialog";
import { useState, useEffect } from "react";
import { useUserWithdrawal } from "~/services/auth";
import { useCustomerAnnouncementUnreadCount } from "~/services/customer/announcement";
import { useLoadingStore } from "../../../stores/loading";
import { useStorage } from "^/common/useStorage";
import { SignType } from "#/unique/domain";
import {
  signOutForOAuth,
  unlinkForOAuth,
  setForceRelogin,
} from "#/auth/signIn";
import { usePlatform } from "^/common/usePlatform";

export const Route = createFileRoute("/customer/layout/user")({
  component: User,
});

interface ProductListType {
  reservationSeq: number;
  companyName: string;
  bizUserName: string;
  companySeq: number;
  companyAddr: string;
  reservTypes: string;
}

function User() {
  const { history } = useRouter();
  const [sns] = useStorage<SignType>("local-sign-sns-type");
  const { petList, mypageMain, isLoading, isFetching } = useMyPageMain();

  const [isWithdrawalConfirmVisible, setIsWithdrawalConfirmVisible] =
    useState(false);
  const [isWithdrawalCompleteVisible, setIsWithdrawalCompleteVisible] =
    useState(false);

  const { mutate: withdrawal } = useUserWithdrawal();
  const { isApp, isAppIOS, isAppAndroid } = usePlatform();
  const { data: announcementUnread } = useCustomerAnnouncementUnreadCount();
  const announcementUnreadCount = announcementUnread?.count ?? 0;

  // 로딩 스토어 사용
  const { toggleLoading } = useLoadingStore();

  // React Query 로딩 상태와 로딩 스토어 연동
  useEffect(() => {
    toggleLoading(isLoading || isFetching);
  }, [isLoading, isFetching, toggleLoading]);

  const handleWithdrawalConfirmOpen = () => {
    setIsWithdrawalConfirmVisible(true);
  };

  const handleWithdrawalConfirmClose = () => {
    setIsWithdrawalConfirmVisible(false);
  };

  const handleWithdrawalCompleteClose = () => {
    setIsWithdrawalCompleteVisible(false);
    // 회원탈퇴 완료 후: 단순 로그아웃이 아니라 SNS 권한까지 끊기 (unlink/revoke)
    handleAfterWithdrawal();
  };

  const handleWithdrawalConfirm = () => {
    setIsWithdrawalConfirmVisible(false);

    withdrawal(undefined, {
      onSuccess: (response) => {
        console.log("회원탈퇴 성공:", response);
        setIsWithdrawalCompleteVisible(true);
      },
      onError: (error) => {
        console.error("회원탈퇴 실패:", error);
        // 에러 처리 (필요시 토스트 메시지 등)
      },
    });
  };

  /** 로컬 토큰/세션 정리 — 웹·앱 공통 */
  const clearLocalSession = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("local-sns-id");
    localStorage.removeItem("local-sign-sns-type");
    localStorage.removeItem("session-fcm");
    sessionStorage.removeItem("session-sign-sns-type");
  };

  /** SNS 타입을 앱 브릿지용 소문자로 변환 (kakao/google/apple/naver) */
  const getSnsTypeForBridge = () => {
    if (sns === "SNS_TYPE_KAKAO") return "kakao";
    if (sns === "SNS_TYPE_GOOGLE") return "google";
    if (sns === "SNS_TYPE_APPLE") return "apple";
    if (sns === "SNS_TYPE_NAVER") return "naver";
    return "";
  };

  /** 일반 로그아웃 — 웹/앱 분리 */
  const handleLogout = async () => {
    const snsType = getSnsTypeForBridge();
    console.log("[customer/user] 로그아웃", { sns, snsType, isApp });

    if (isApp) {
      // 앱: 네이티브 브릿지가 SNS 로그아웃 처리
      if (isAppAndroid) {
        console.log("→ Android 앱 appLoginOut", snsType);
        window.mung.appLoginOut(snsType);
      } else if (isAppIOS) {
        console.log("→ iOS 앱 appLoginOut", snsType);
        window.webkit?.messageHandlers?.mung?.postMessage([
          "appLoginOut",
          snsType,
        ]);
      }
    } else {
      // 웹: 직접 SNS OAuth 로그아웃 호출
      console.log("→ 웹 OAuth 로그아웃");
      try {
        await signOutForOAuth();
      } catch (error) {
        console.error("OAuth 로그아웃 실패:", error);
      }
    }

    clearLocalSession();

    // 다음 로그인 시 카카오/구글 등 SSO 자동로그인 방지 — 강제 재인증 플래그 세팅
    setForceRelogin();

    // 메인 페이지로 이동
    history.push("/");
  };

  /** 회원탈퇴 후 처리 — 단순 로그아웃이 아니라 SNS 권한 자체를 끊음 */
  const handleAfterWithdrawal = async () => {
    const snsType = getSnsTypeForBridge();
    console.log("[customer/user] 회원탈퇴 후 SNS 연결 끊기", {
      sns,
      snsType,
      isApp,
    });

    if (isApp) {
      // 앱: 네이티브가 SNS 회원탈퇴(unlink) 처리하도록
      if (isAppAndroid && typeof window.mung?.appUnlink === "function") {
        window.mung.appUnlink(snsType);
      } else if (isAppAndroid) {
        // appUnlink 미구현이면 fallback 으로 logout 호출
        window.mung.appLoginOut(snsType);
      } else if (isAppIOS) {
        // iOS 도 동일: appUnlink 우선, 없으면 appLoginOut
        window.webkit?.messageHandlers?.mung?.postMessage([
          "appUnlink",
          snsType,
        ]);
      }
    } else {
      // 웹: SNS 권한 끊기 (revoke / unlink)
      try {
        await unlinkForOAuth();
      } catch (error) {
        console.error("SNS 연결 끊기 실패:", error);
      }
    }

    clearLocalSession();
    history.push("/");
  };
  // console.log(mypageMain)
  return (
    <div className="px-3 pt-5 pb-28 flex-auto bg-[#F5F5F5] gap-2">
      <div className="bg-[#F5F5F5] flex-auto">
        {/* 홍길동님 반가워요 */}
        <div className="px-1">
          <div className="font-medium text-[18px]">
            {mypageMain?.userName ?? "고객"}님 반가워요
          </div>
          {/* <div className="text-gray-500 text-[16px]">
            현재{" "}
            <span className="text-orange-400">
              {mypageMain?.coinAmount.toLocaleString() ?? 0}
            </span>{" "}
            멍코인을 갖고 있어요
          </div> */}
        </div>

        <div className="my-2">
          <div className="mt-3 mb-2 ml-1 font-medium">우리가족</div>
          <SwiperXBox data={petList ?? []} />
        </div>

        {/* 이용중인 서비스 */}
        <div className="mt-4">
          <div className="mb-2 font-medium">
            {mypageMain && mypageMain.productList.length > 0
              ? "이용중인 서비스"
              : "이용중인 서비스가 없습니다."}
          </div>
          {mypageMain?.productList.map((item: ProductListType) => (
            <div
              className="flex items-center py-1.5 bg-white rounded-xl my-2 border-2 border-[#E9E9E9] h-[114px]"
              key={item.reservationSeq}
              onClick={() =>
                history.push(
                  `/customer/layout/home-serach-detail?companySeq=${item.companySeq}`,
                )
              }
            >
              <div className="flex items-center bg-white rounded-xl">
                <img
                  src={a1}
                  alt="유치원 이미지"
                  className="mx-5 my-2 bg-gray-50 rounded-full w-15 h-15"
                />
                <div className="ml-2 text-left">
                  <p className="py-1 text-base font-bold">
                    {item.companyName ?? ""}
                  </p>
                  <p className="mb-1 text-sm font-medium">
                    {item.companyAddr ?? ""}
                  </p>
                  <p className="text-sm text-orange-400">
                    {item.reservTypes ?? ""}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {/* 반복 */}
          {/* <div className="flex items-center py-1.5 bg-white rounded-xl my-2 border-2 border-[#E9E9E9] h-[114px]">
            <div className="flex items-center bg-white rounded-xl">
              <img
                src={a1}
                alt="유치원 이미지"
                className="mx-5 my-2 bg-gray-50 rounded-full w-15 h-15"
              />
              <div className="ml-2 text-left">
                <p className="py-2 text-base font-bold">아무개 강아지 유치원</p>
                <p className="text-sm font-medium">원장:사람 | 희동이반반</p>
                <p className="text-sm text-orange-400">아무개 강아지 유치원</p>
              </div>
            </div>
          </div>
          <div className="flex items-center py-1.5 bg-white rounded-xl my-2 border-2 border-[#E9E9E9] h-[114px]">
            <div className="flex items-center bg-white rounded-xl">
              <img
                src={a1}
                alt="유치원 이미지"
                className="mx-5 my-2 bg-gray-50 rounded-full w-15 h-15"
              />
              <div className="ml-2 text-left">
                <p className="py-2 text-base font-bold">아무개 강아지 유치원</p>
                <p className="text-sm font-medium">원장:사람 | 희동이반반</p>
                <p className="text-sm text-orange-400">아무개 강아지 유치원</p>
              </div>
            </div>
          </div> */}
          {/* 반복 */}
        </div>

        {/* 사용자 설정 */}
        <div className="mt-4">
          <div className="mb-2 font-medium">사용자 설정</div>
          <div className="bg-white border-2 border-[#E9E9E9] rounded-xl">
            <button
              className="flex justify-between items-center px-4 py-2 w-full"
              onClick={() => history.push("/customer/layout/user-info-Chg")}
            >
              <span>계정 정보 변경</span>
              <img src={chevironRight} alt="계정 정보 변경" />
            </button>
            <button
              className="flex justify-between items-center px-4 py-2 w-full"
              onClick={() =>
                history.push("/customer/layout/user-guardian-link")
              }
            >
              <span>보호자 연동</span>
              <img src={chevironRight} alt="보호자 연동" />
            </button>
            {/* <button className="flex justify-between items-center px-4 py-2 w-full border-b">
              <span>결제 정보 관리</span>
              <img src={chevironRight} alt="결제 정보 관리" />
            </button> */}
            <button
              className="flex justify-between items-center px-4 py-2 w-full"
              onClick={() => history.push("/customer/layout/user-push")}
            >
              <span>알림</span>
              <img src={chevironRight} alt="알림" />
            </button>
            {/* <button
              className="flex justify-between items-center px-4 py-2 w-full"
              onClick={() => history.push("/customer/layout/user-buy-coin")}
            >
              <span>멍코인 구매</span>
              <img src={chevironRight} alt="멍코인 구매" />
            </button> */}
            <button
              className="flex justify-between items-center px-4 py-2 w-full"
              onClick={() =>
                history.push("/customer/layout/user-subscriptions")
              }
            >
              <span>이용권 조회</span>
              <img src={chevironRight} alt="이용권 조회" />
            </button>
            <button
              className="flex justify-between items-center px-4 py-2 w-full"
              onClick={() =>
                history.push("/customer/layout/user-usage-history")
              }
            >
              <span>이용 내역</span>
              <img src={chevironRight} alt="이용 내역" />
            </button>
            <button
              className="flex justify-between items-center px-4 py-3 w-full"
              onClick={() => history.push("/customer/layout/user-contract")}
            >
              <span>계약서 조회</span>
              <img src={chevironRight} alt="계약서 조회" />
            </button>
            <button
              className="flex justify-between items-center px-4 py-3 w-full"
              onClick={() =>
                history.push("/customer/layout/contract-inbox" as any)
              }
            >
              <span>서명 요청함 ✍️</span>
              <img src={chevironRight} alt="서명 요청함" />
            </button>
            <button
              className="flex justify-between items-center px-4 py-3 w-full"
              onClick={() => history.push("/policy" as any)}
            >
              <span>약관 및 정책</span>
              <img src={chevironRight} alt="약관 및 정책" />
            </button>
          </div>
        </div>

        {/* 반려동물 정보 */}
        <div className="mt-4">
          <div className="mb-2 font-medium">반려동물 정보</div>
          <div className="bg-white border-2 border-[#E9E9E9] rounded-xl">
            <button
              className="flex justify-between items-center px-4 py-3 w-full"
              onClick={() => history.push("/customer/layout/user-pet-info")}
            >
              <span>반려동물 정보</span>
              <img src={chevironRight} alt="반려동물 정보" />
            </button>
          </div>
        </div>
        {/* 시스템 정보 */}
        <div className="mt-4">
          <div className="mb-2 font-medium">시스템 정보</div>
          <div className="bg-white border-2 border-[#E9E9E9] rounded-xl">
            <button
              className="flex justify-between items-center px-4 py-3 w-full"
              onClick={() => history.push("/customer/layout/user-announcement")}
            >
              <span>공지사항</span>
              <div className="flex items-center gap-2">
                {announcementUnreadCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold">
                    {announcementUnreadCount > 99
                      ? "99+"
                      : announcementUnreadCount}
                  </span>
                )}
                <img src={chevironRight} alt="공지사항" />
              </div>
            </button>
            <button
              className="flex justify-between items-center px-4 py-3 w-full"
              onClick={() => history.push("/customer/layout/inquiry-history")}
            >
              <span>문의 내역</span>
              <img src={chevironRight} alt="문의 내역" />
            </button>
          </div>
        </div>

        {/* 로그아웃 */}
        <div className="flex justify-between px-4 pb-4 mt-10">
          <button
            className="text-sm text-gray-400 text-[#666666]"
            onClick={() => handleLogout()}
          >
            로그아웃
          </button>
          <button
            className="text-sm text-gray-400 text-[#666666]"
            onClick={handleWithdrawalConfirmOpen}
          >
            회원탈퇴
          </button>
        </div>

        {/* 회원탈퇴 확인 모달 */}
        <Dialog
          visible={isWithdrawalConfirmVisible}
          onClose={handleWithdrawalConfirmClose}
          persist={false}
        >
          <div className="flex flex-col gap-1">
            <div className="flex flex-col mb-2">
              <div className="text-lg font-semibold text-center">회원 탈퇴</div>
            </div>
            <div className="flex flex-col mb-2">
              <div className="text-sm text-center text-[#666666]">
                회원 탈퇴 시 모든 개인정보 및 사업자 정보를
              </div>
              <div className="text-sm text-center text-[#666666]">
                더이상 확인하실 수 없으며, 정산정보가 삭제됩니다.
              </div>
              <div className="text-sm text-center text-[#666666] mt-2">
                탈퇴 후에는 삭제된 정보를 복구할 수 없습니다.
              </div>
            </div>
            <div className="flex gap-3 mt-2">
              <button
                className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
                onClick={handleWithdrawalConfirmClose}
              >
                취소
              </button>
              <button
                className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                onClick={handleWithdrawalConfirm}
              >
                확인
              </button>
            </div>
          </div>
        </Dialog>

        {/* 회원탈퇴 완료 모달 */}
        <Dialog
          visible={isWithdrawalCompleteVisible}
          onClose={handleWithdrawalCompleteClose}
          persist={false}
        >
          <div className="flex flex-col gap-1">
            <div className="flex flex-col mb-2">
              <div className="text-lg font-semibold text-center">회원 탈퇴</div>
            </div>
            <div className="flex flex-col mb-2">
              <div className="text-sm text-center text-[#666666]">
                회원 탈퇴가 완료되었습니다.
              </div>
            </div>
            <div className="flex gap-3 mt-2">
              <button
                className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                onClick={handleWithdrawalCompleteClose}
              >
                확인
              </button>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
}
